import { useState } from 'react';
import api from '../utils/api';
import { toast } from 'sonner';

/**
 * Encapsulates all loyalty-related state used by the Revenue page:
 *  - lookup by membership number
 *  - redeem points
 *  - award points after a revenue is created
 *  - create receipt-attachment for new memberships (<3 days)
 */
export default function useLoyaltyForRevenue(language) {
  const [member, setMember] = useState(null);
  const [looking, setLooking] = useState(false);
  const [redeemPoints, setRedeemPoints] = useState('');

  const lookup = async (num) => {
    if (!num) {
      setMember(null);
      return;
    }
    setLooking(true);
    try {
      const res = await api.get(`/loyalty/lookup-by-membership/${encodeURIComponent(num)}`);
      setMember(res.data);
    } catch (err) {
      setMember(null);
      if (err.response?.status === 404) {
        toast.error(language === 'ar' ? 'رقم العضوية غير موجود أو غير معتمد' : 'Membership not found');
      } else if (err.response?.status === 403) {
        toast.error(
          err.response?.data?.detail ||
            (language === 'ar' ? 'العضوية انتهت فترة سماحها' : 'Membership grace period expired')
        );
      }
    } finally {
      setLooking(false);
    }
  };

  const redeem = async (memberId, qty, note) => {
    if (!memberId || !qty) return false;
    try {
      const res = await api.post(`/loyalty/members/${memberId}/redeem`, {
        points: qty,
        note: note || '',
      });
      toast.success(
        res.data?.message || (language === 'ar' ? 'تم استبدال النقاط' : 'Points redeemed')
      );
      return true;
    } catch {
      toast.warning(
        language === 'ar'
          ? 'تم حفظ الإيراد لكن فشل استبدال النقاط'
          : 'Revenue saved but redemption failed'
      );
      return false;
    }
  };

  const award = async (memberId, points, payload) => {
    if (!memberId || !points) return false;
    try {
      const res = await api.post(`/loyalty/members/${memberId}/award`, {
        points: Math.floor(points),
        service_name: payload.service_name || (language === 'ar' ? 'خدمة' : 'Service'),
        amount_aed: payload.amount_aed,
        note: language === 'ar' ? 'إضافة تلقائية من الإيراد' : 'Auto from revenue',
        source: 'revenue',
      });
      toast.success(
        res.data?.message || (language === 'ar' ? 'تمت إضافة النقاط' : 'Points awarded')
      );
      return true;
    } catch {
      toast.warning(
        language === 'ar' ? 'تم حفظ الإيراد لكن فشلت إضافة النقاط' : 'Revenue saved but points failed'
      );
      return false;
    }
  };

  const createReceiptAttachment = async (payload) => {
    try {
      await api.post('/loyalty/receipt-attachments', payload);
      toast.info(
        language === 'ar'
          ? '⏳ تم تسجيل ربط العضوية الجديدة — بانتظار اعتماد المدير (خلال 7 أيام)'
          : '⏳ Membership attachment pending admin approval (within 7 days)'
      );
      return true;
    } catch (err) {
      console.log('Receipt attachment failed', err);
      return false;
    }
  };

  const reset = () => {
    setMember(null);
    setRedeemPoints('');
  };

  return {
    member,
    setMember,
    looking,
    redeemPoints,
    setRedeemPoints,
    lookup,
    redeem,
    award,
    createReceiptAttachment,
    reset,
  };
}
